import React, { useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Loader2, AlertTriangle } from "lucide-react";

/**
 * AISummaryCard — reusable inline AI generator card.
 *
 * Props:
 *   title:        section title (e.g. "AI QA Summary")
 *   endpoint:     POST endpoint (e.g. "/ai/qa-summary")
 *   payload:      object posted to endpoint (without `regenerate`)
 *   render:       function(result) → ReactNode that pretty-prints the AI JSON
 *   testIdPrefix: testid prefix for buttons/blocks
 */
export default function AISummaryCard({ title, endpoint, payload, render, testIdPrefix = "ai-card" }) {
  const [data, setData] = useState(null);
  const [cached, setCached] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async (regenerate) => {
    setLoading(true); setError(null);
    try {
      const { data: resp } = await api.post(endpoint, { ...payload, regenerate });
      // Server may return either {summary: ...} or {recommendations: ...}
      const body = resp.summary || resp.recommendations || resp;
      setData(body);
      setCached(!!resp.cached);
      setGeneratedAt(resp.generated_at || null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 429 || status === 402) {
        setError(detail || "AI limit/budget reached.");
      } else if (status === 404) {
        setError(detail || "No record exists yet for AI to summarize.");
      } else {
        setError(detail || "AI request failed.");
      }
      toast.error(detail || "AI request failed");
    } finally {
      setLoading(false);
    }
  }, [endpoint, payload]);

  return (
    <div className="nv-card p-5 space-y-3" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Sparkles size={16} className="text-indigo-600" />
          <span>{title}</span>
          {cached && (
            <span className="text-[11.5px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
              Cached · {generatedAt ? new Date(generatedAt).toLocaleString() : "saved"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!data && (
            <button
              onClick={() => run(false)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md text-xs font-semibold disabled:opacity-60"
              data-testid={`${testIdPrefix}-generate-btn`}
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {loading ? "Generating…" : "Generate AI summary"}
            </button>
          )}
          {data && (
            <button
              onClick={() => run(true)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-md text-xs font-semibold disabled:opacity-60"
              data-testid={`${testIdPrefix}-regenerate-btn`}
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {loading ? "Generating…" : "Regenerate"}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-rose-50 border border-rose-200 text-rose-800 px-3 py-2 text-xs flex items-start gap-2" data-testid={`${testIdPrefix}-error`}>
          <AlertTriangle size={14} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {data && <div className="text-sm text-slate-700">{render(data)}</div>}
      {!data && !error && !loading && (
        <p className="text-xs text-slate-500">Click <em>Generate AI summary</em> to produce a permission-scoped, GPT-5.2 analysis. Saved automatically; can be regenerated.</p>
      )}
    </div>
  );
}
